import { OrderedSet, Map, List, Record, OrderedMap } from 'immutable';
import omit from 'lodash/omit';
import { GroupMember } from 'records/GroupMember';
import { Color, RawColor } from 'records/Color';
import { RawUser, User } from 'records/User';

export type PunchDisciplines = OrderedSet<string>;
export type PunchAssignees = OrderedSet<string>;
export type PunchGroupAssignees = OrderedSet<string>;
export type PunchGroupMemberAssignees = Map<string, GroupMember[]>;

export type PunchId = string;

export enum PunchStatusValue {
  OPEN = 'OPEN',
  READY = 'READY_FOR_REVIEW',
  OUT_OF_SCOPE = 'OUT_OF_SCOPE',
  CLOSED = 'CLOSED',
}

export interface RawPunchStatus {
  status: PunchStatusValue;
  displayString: string;
  statusColor: RawColor;
}

export interface PunchStatus {
  status: PunchStatusValue;
  displayString: string;
  statusColor: Color;
}

const punchPriorities = ['Hot', 'High', 'Normal', 'Low'];

const getPriority = (rawPriority: string) => {
  if (punchPriorities.indexOf(rawPriority) >= 0) {
    return rawPriority;
  }
  return 'Normal';
};

export interface RawPunch {
  id: PunchId;
  projectid: string;
  description: string;
  dbcreateddate: number;
  issuetitle: string;
  completeddate: number;
  author: RawUser;
  clientcreateddate: number;
  location: string;
  assignees: string[];
  groupAssignees: string[];
  duedate: number;
  disciplines: string[];
  comment: string;
  priority: string;
  number: number;
  photoids: string[];
  markupids: string[];
  issueStatus: RawPunchStatus;
}

export interface PunchData {
  id: PunchId;
  projectid: string;
  description: string;
  dbcreateddate: number;
  issuetitle: string;
  completeddate: number;
  author: User;
  clientcreateddate: number;
  location: string;
  assignees: PunchAssignees;
  groupAssignees: PunchGroupAssignees;
  duedate: number;
  disciplines: PunchDisciplines;
  comment: string;
  priority: string;
  number: number;
  photoids: List<string>;
  markupids: List<string>;
  status: PunchStatusValue;
  displayString: string;
  statusColor: Color;
}

export interface RawUpdatePunch {
  id: string;
  projectid: string;
  description: string;
  dbcreateddate: number;
  issuetitle: string;
  completeddate: number;
  authorid: string;
  clientcreateddate: number;
  location: string;
  assignees: string[];
  groupAssignees: string[];
  duedate: number;
  disciplines: string[];
  comment: string;
  priority: string;
  number?: number;
  statusValue: PunchStatusValue;
  // photos: string[];
  markups: string[];
}

const defaultValues: PunchData = {
  id: '',
  projectid: '',
  description: '',
  dbcreateddate: 0,
  issuetitle: '',
  completeddate: 0,
  author: new User(),
  clientcreateddate: 0,
  location: '',
  assignees: OrderedSet<string>(),
  groupAssignees: OrderedSet<string>(),
  duedate: 0,
  disciplines: OrderedSet<string>(),
  comment: '',
  priority: 'Normal',
  number: 0,
  photoids: List<string>(),
  markupids: List<string>(),
  status: PunchStatusValue.OPEN,
  displayString: 'OPEN',
  statusColor: new Color(),
};

export const PunchFactory = Record(defaultValues, 'Punch');

export class Punch extends PunchFactory {
  toRawUpdatePunch(): RawUpdatePunch {
    const rawPunch = this.toJS();
    return {
      ...omit(rawPunch, [
        'author',
        'number',
        'photoids',
        'statusColor',
        'status',
        'displayString',
        'markupids',
      ]),
      authorid: this.author.id,
      statusValue: this.status,
      markups: this.markupids.toArray(),
    };
  }

  // Need to handle immutable lists in PunchData....
  static deserialize(rawPunch: RawPunch): Punch {
    return new Punch({
      ...rawPunch,
      author: User.deserialize(rawPunch.author),
      priority: getPriority(rawPunch.priority),
      assignees: OrderedSet(rawPunch.assignees),
      groupAssignees: OrderedSet(rawPunch.groupAssignees),
      disciplines: OrderedSet(rawPunch.disciplines),
      photoids: List(rawPunch.photoids),
      markupids: List(rawPunch.markupids),
      displayString: rawPunch.issueStatus.displayString,
      status: rawPunch.issueStatus.status,
      statusColor: Color.deserialize(rawPunch.issueStatus.statusColor),
    });
  }
}

export type PunchMap = Map<string, Punch>;

export type PunchOrderedMap = OrderedMap<PunchId, Punch>;

export type PunchList = List<Punch>;
